"use client";

import { Card, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { CUSTOMER_TEMPLATE_STATUS_OPTIONS } from "@/constants/message-template.constants";
import { ICustomerTemplate } from "@/interfaces/customer-template.interface";
import { MessageTemplateMessages } from "@/messages/message-template/message-template.messages";
import { FilterProperty, FilterType, IFilter } from "@/types/filter.types";

type Props = {
  messages: MessageTemplateMessages;
};

const GRID_SIZES = {
  LARGE: { lg: 4, md: 4, sm: 6, xs: 12 },
  SMALL: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

const NOT_FOUND_INDEX = -1;
const EMPTY_STRING = "";

type TemplateFilterKey = FilterProperty<ICustomerTemplate>;

function MessageTemplateFiltersForm({ messages }: Props) {
  const { values, setValues, submitForm } = useFormikContext<IFilter<ICustomerTemplate>[]>();

  const getValueFromProperty = (property: TemplateFilterKey) =>
    values.find((filter) => filter.property === property);

  const handleChange = (property: TemplateFilterKey, value: string, filterType: FilterType) => {
    setValues((previousFilters) => {
      const filters = [...previousFilters];
      const existingIndex = filters.findIndex((filter) => filter.property === property);

      if (existingIndex !== NOT_FOUND_INDEX) filters.splice(existingIndex, 1);

      if (!value) return filters;

      return [...filters, { property, filterType, value }];
    });
  };

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              name="name"
              label={messages.filterNameLabel}
              fullWidth
              value={getValueFromProperty("name")?.value ?? EMPTY_STRING}
              onChange={(e) => handleChange("name", e.target.value, FilterType.Contains)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              name="type"
              label={messages.filterTypeLabel}
              fullWidth
              value={getValueFromProperty("type")?.value ?? EMPTY_STRING}
              onChange={(e) => handleChange("type", e.target.value, FilterType.Contains)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              name="status"
              label={messages.filterStatusLabel}
              select
              fullWidth
              variant="outlined"
              value={getValueFromProperty("status")?.value ?? EMPTY_STRING}
              onChange={(e) => handleChange("status", e.target.value, FilterType.Equals)}
              slotProps={{ inputLabel: { shrink: true } }}
            >
              {CUSTOMER_TEMPLATE_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={() => setValues([])} fullWidth>
                  {messages.clearFiltersButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default MessageTemplateFiltersForm;
